/* eslint-disable no-restricted-imports */
import {
	DpIAOutfitCarousel,
	DpIARecommendationResponse,
} from 'integration/generated/transactions/data-contracts';
import valueIsArray from 'integration/utils/typeAssertions/valueIsArray';
import valueIsRecord from 'integration/utils/typeAssertions/valueIsRecord';
import valueIsString from 'integration/utils/typeAssertions/valueIsString';

type IARecommendationType = 'recommendation' | 'outfit' | 'complements' | 'sameBrand' | 'family';

const idsOf = (list: unknown): string[] => {
	if (!valueIsArray(list)) {
		return [];
	}

	return list.map((item) => (valueIsRecord(item) ? item?.id : undefined)).filter(valueIsString);
};

const innerDataOf = (data: unknown): Record<string, unknown> | undefined => {
	const innerData = valueIsRecord(data) ? (data as DpIARecommendationResponse).data : undefined;
	return valueIsRecord(innerData) ? (innerData as Record<string, unknown>) : undefined;
};

const outfitsOf = (data: unknown): DpIAOutfitCarousel[] => {
	const outfits = innerDataOf(data)?.outfits;
	return valueIsArray(outfits) ? (outfits as DpIAOutfitCarousel[]) : [];
};

const outfitIds = (inner: Record<string, unknown>): string[] => {
	const [carousel] = valueIsArray(inner.outfits) ? (inner.outfits as DpIAOutfitCarousel[]) : [];
	if (!carousel) {
		return idsOf(inner.outfit);
	}

	const ids = [carousel.anchor?.id, ...idsOf(carousel.items)].filter(valueIsString);
	return Array.from(new Set(ids));
};

export const partNumbersIARecommendationExtractorByUnknown = (
	type: IARecommendationType,
	data: unknown
): string[] => {
	const inner = innerDataOf(data);
	if (!inner) {
		return [];
	}

	if (type === 'outfit') {
		return outfitIds(inner);
	}

	return idsOf(inner[type === 'recommendation' ? 'recommendations' : type]);
};

export const allOutfitPartNumbers = (data: unknown): string[] =>
	Array.from(new Set(outfitsOf(data).flatMap((outfit) => idsOf(outfit?.items))));

export const selectMostAvailableOutfit = <T extends { partNumber: string }>(
	products: T[],
	data: unknown
): T[] => {
	const byPartNumber = new Map(products.map((product) => [product.partNumber, product]));
	const [best] = outfitsOf(data)
		.map((outfit, index) => ({
			index,
			score: typeof outfit?.score === 'number' ? outfit.score : 0,
			products: idsOf(outfit?.items).flatMap((id) => {
				const product = byPartNumber.get(id);
				return product ? [product] : [];
			}),
		}))
		.sort(
			(a, b) => b.products.length - a.products.length || b.score - a.score || a.index - b.index
		);
	return best?.products ?? [];
};
